/* eslint-disable fp/no-mutating-methods */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material'
import React, { ReactElement, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchTitleScreens,
  fetchVideos,
  fetchTextContent,
  fetchQuizzes,
  fetchStoryboardSteps,
  selectTitleScreenByClassID,
  selectVideosByClassID,
  selectTextContentByClassID,
  selectQuizByClassID,
  selectStoryboardStepsByClassID,
} from 'src/v3/create/create.slice'
import { LeftArrowIcon } from 'src/v3/core/icons'
import CheckIcon from '@mui/icons-material/Check'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import { ClassUpdated as Class } from 'src/v3/core/types'

interface Props {
  selectedClass?: Class
  setStep: React.Dispatch<
    React.SetStateAction<
      | 'basic-info'
      | 'title-screen'
      | 'content'
      | 'quiz'
      | 'storyboard'
      | 'preview'
    >
  >
  onFinish?: () => void
}

export default ({ selectedClass, setStep, onFinish }: Props): ReactElement => {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()

  let titleScreen = useAppSelector(
    selectTitleScreenByClassID(selectedClass?.id || ''),
  )
  let videos = useAppSelector(selectVideosByClassID(selectedClass?.id || ''))
  let textContent = useAppSelector(
    selectTextContentByClassID(selectedClass?.id || ''),
  )
  let quiz = useAppSelector(selectQuizByClassID(selectedClass?.id || ''))
  let storyboardSteps = useAppSelector(
    selectStoryboardStepsByClassID(selectedClass?.id || ''),
  )

  let getStepName = (step: any) => {
    if (step.type === 'titleScreen' || step.titleScreenID) {
      return `${selectedClass?.name} Title Screen`
    }
    if (step.type === 'video' || step.videoID) {
      let video = videos.find((v) => v.id === step.videoID)
      return video?.name || 'Unknown Video'
    }
    if (step.type === 'textContent' || step.textContentID) {
      let content = textContent.find((tc) => tc.id === step.textContentID)
      return content?.name || 'Unknown Text Content'
    }
    if (step.type === 'quiz' || step.quizID) {
      return `${selectedClass?.name} Quiz`
    }
    return 'Unknown Step'
  }

  useEffect(() => {
    if (selectedClass) {
      dispatch(fetchTitleScreens(selectedClass.id))
      dispatch(fetchVideos())
      dispatch(fetchTextContent(selectedClass.id))
      dispatch(fetchQuizzes(selectedClass.id))
      dispatch(fetchStoryboardSteps())
    }
  }, [selectedClass])

  let handleFinish = () => {
    if (onFinish) {
      onFinish()
    } else {
      // Fallback navigation
      navigate('/create')
    }
  }

  if (!selectedClass) {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please complete the Basic Info step first.
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('basic-info')}
          >
            Go to Basic Info
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
        Preview & Finish
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Review your class setup and student experience.
      </Typography>

      {/* Class Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Class Summary
          </Typography>

          <Box className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <Box>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1">{selectedClass.name}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1">
                {selectedClass.description}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Course ID
              </Typography>
              <Typography variant="body1">{selectedClass.courseID}</Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Sequence
              </Typography>
              <Typography variant="body1">{selectedClass.sequence}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Content Overview */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Content Overview
          </Typography>

          <Box>
            <Box>
              <Box sx={{ textAlign: 'center' }}>
                {titleScreen ? (
                  <CheckCircleIcon
                    sx={{ fontSize: 48, color: 'success.main' }}
                  />
                ) : (
                  <CancelIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                )}
                <Typography variant="body2" color="text.secondary">
                  Title Screen
                </Typography>
              </Box>
            </Box>

            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {videos.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Videos
                </Typography>
              </Box>
            </Box>

            <Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" color="primary">
                  {textContent?.length || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Text Content
                </Typography>
              </Box>
            </Box>

            <Box>
              <Box sx={{ textAlign: 'center' }}>
                {quiz ? (
                  <CheckCircleIcon
                    sx={{ fontSize: 48, color: 'success.main' }}
                  />
                ) : (
                  <CancelIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                )}
                <Typography variant="body2" color="text.secondary">
                  Quiz
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Storyboard Summary */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Storyboard ({storyboardSteps.length} steps)
          </Typography>

          {storyboardSteps.length === 0 ? (
            <Alert severity="warning">
              No storyboard created yet. Students won't be able to access this
              class until you create a storyboard that arranges the content
              flow.
            </Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Student learning flow:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {storyboardSteps
                  .sort((a, b) => a.stepIndex - b.stepIndex)
                  .map((step: any, index) => (
                    <Typography key={step.id} variant="body2">
                      {index + 1}. {getStepName(step)}
                    </Typography>
                  ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Readiness Check */}
      {storyboardSteps.length > 0 ? (
        <Alert severity="success" sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Class is ready for students!
          </Typography>
          <Typography variant="body2">
            Your class has been set up with all necessary content and
            storyboard.
          </Typography>
        </Alert>
      ) : (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Class setup incomplete
          </Typography>
          <Typography variant="body2">
            Please create a storyboard to arrange the content flow before it can
            be used in a published course.
          </Typography>
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/create/v4')}>
            Save and Exit
          </Button>
          <Button
            variant="outlined"
            startIcon={<LeftArrowIcon />}
            onClick={() => setStep('storyboard')}
          >
            Back to Storyboard
          </Button>
        </Box>

        <Button
          variant="contained"
          color="success"
          endIcon={<CheckIcon />}
          onClick={handleFinish}
        >
          Finish Class Setup
        </Button>
      </Box>
    </Box>
  )
}
